import { VOSK_GRAMMAR } from './lib/vocabulary';

// A result is stale if every word in it ended before the current listening
// window opened. Vosk timestamps words on the recognizer's own audio timeline
// (seconds of audio fed so far), so this survives worker lag: on slow devices
// the WASM decoder falls behind real time and results for the previous word
// can arrive after the next one has started.
export const isStaleResult = (words, listenStartSeconds) =>
  words.length > 0 && words[words.length - 1].end <= listenStartSeconds + 0.001;

export const VOSK_MODEL_URL = 'https://ccoreilly.github.io/vosk-browser/models/vosk-model-small-en-us-0.15.tar.gz';

// iPhone and iPad Safari (iPadOS reports itself as a Mac, hence the touch check).
export const isIOS = () => typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export async function createVoskEngine({ onResult, onPartial, onSilence, onLost }) {
  if (!navigator.mediaDevices?.getUserMedia || !(window.AudioContext || window.webkitAudioContext)) {
    throw new Error('This browser does not expose the required microphone audio APIs.');
  }

  // On iOS, echo cancellation puts the whole audio session into voice-processing
  // mode, which ducks every sound the page plays to intercom volume. Listening
  // never overlaps playback here, so the AEC buys nothing on iOS; leave it off.
  const ios = isIOS();
  const stream = await navigator.mediaDevices.getUserMedia({
    video: false,
    audio: { echoCancellation: !ios, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
  });
  // Created after the mic opens: iOS re-negotiates the hardware sample rate when
  // capture starts, and a context made earlier is left silent.
  const Context = window.AudioContext || window.webkitAudioContext;
  const audioContext = new Context();
  await audioContext.resume();

  // iOS suspends the context ("interrupted") when the tab is hidden, the screen
  // locks, Siri or a call cuts in, or another audio route starts. It does not
  // come back by itself; nudge it whenever the page or the user resurfaces.
  const wake = () => { if (audioContext.state !== 'running' && audioContext.state !== 'closed') audioContext.resume().catch(() => {}); };
  const wakeEvents = [[document, 'visibilitychange'], [window, 'pageshow'], [window, 'focus'], [document, 'touchend'], [document, 'pointerup']];
  wakeEvents.forEach(([target, name]) => target.addEventListener(name, wake, { passive: true }));
  // iOS closes the capture track outright after the page has been in the
  // background a while; the engine is dead from then on and must be rebuilt.
  let lost = false;
  stream.getAudioTracks().forEach(track => track.addEventListener('ended', () => { if (!lost) { lost = true; onLost?.(); } }));

  let model;
  try {
    const { createModel } = await import('vosk-browser');
    model = await createModel(VOSK_MODEL_URL, -1);
  } catch (error) {
    stream.getTracks().forEach(track => track.stop());
    await audioContext.close();
    throw new Error('The offline speech model could not be loaded.', { cause: error });
  }

  const recognizer = new model.KaldiRecognizer(audioContext.sampleRate, JSON.stringify(VOSK_GRAMMAR));
  recognizer.setWords(true);
  let listening = false;
  let feeding = true;           // false while a flush drains the worker queue
  let flushCount = 0;
  let sentSamples = 0;          // audio samples handed to the recognizer so far
  let listenStartSeconds = 0;   // recognizer-timeline position when start() was last called
  let heardSpeech = false;
  let silenceStartedAt = 0;
  let silenceReported = false;

  recognizer.on('result', message => {
    if (!listening) return;
    const words = message.result?.result || [];
    if (isStaleResult(words, listenStartSeconds)) return;
    const text = message.result?.text?.trim();
    if (text && text !== '[unk]') onResult(text, words);
    else if (text === '[unk]') onPartial('That sounded like a word. Say one letter at a time.');
  });
  recognizer.on('partialresult', message => {
    if (!listening) return;
    const partial = message.result?.partial?.trim();
    if (partial && partial !== '[unk]') onPartial(`Hearing “${partial}”…`);
  });
  recognizer.on('error', message => {
    if (listening) onPartial(message.error || 'The offline recognizer missed that.');
  });

  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const silentOutput = audioContext.createGain();
  silentOutput.gain.value = 0;
  processor.onaudioprocess = event => {
    if (!listening || !feeding) return;
    recognizer.acceptWaveform(event.inputBuffer);
    sentSamples += event.inputBuffer.length;

    const samples = event.inputBuffer.getChannelData(0);
    let energy = 0;
    for (let i = 0; i < samples.length; i += 1) energy += samples[i] * samples[i];
    const rms = Math.sqrt(energy / samples.length);
    const now = performance.now();
    if (rms > 0.018) {
      heardSpeech = true;
      silenceStartedAt = 0;
      silenceReported = false;
    } else if (heardSpeech && !silenceReported) {
      if (!silenceStartedAt) silenceStartedAt = now;
      if (now - silenceStartedAt >= 2200) {
        silenceReported = true;
        recognizer.retrieveFinalResult();
        onSilence();
      }
    }
  };
  source.connect(processor);
  processor.connect(silentOutput);
  silentOutput.connect(audioContext.destination);

  // Word clips play through this same context: on iOS a separate <audio>
  // element needs its own user gesture (silent otherwise) and starts a second
  // audio route that interrupts the capture context (mic stops listening).
  const clipCache = new Map();
  const loadClip = (url) => {
    if (!clipCache.has(url)) {
      clipCache.set(url, fetch(url).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.arrayBuffer(); })
        .then(data => new Promise((resolve, reject) => audioContext.decodeAudioData(data, resolve, reject)))
        .catch(error => { clipCache.delete(url); throw error; }));
    }
    return clipCache.get(url);
  };
  const playback = audioContext.createGain();
  playback.connect(audioContext.destination);
  let playing = null;

  return {
    // Fetch and decode a clip ahead of time so playClip can start it on cue.
    preloadClip(url) { return loadClip(url).catch(() => {}); },
    // Resolves when the clip has finished; rejects if it cannot be fetched,
    // decoded or started. `onStart` fires when sound actually begins.
    async playClip(url, onStart) {
      const buffer = await loadClip(url);
      this.stopClip();
      wake();
      return new Promise((resolve, reject) => {
        const node = audioContext.createBufferSource();
        node.buffer = buffer;
        node.connect(playback);
        node.onended = () => { if (playing === node) playing = null; resolve(); };
        playing = node;
        try { node.start(); } catch (error) { playing = null; reject(error); return; }
        onStart?.();
      });
    },
    stopClip() {
      if (!playing) return;
      const node = playing; playing = null;
      node.onended = null;
      try { node.stop(); } catch (_) {}
    },
    start() {
      wake();
      listening = true;
      listenStartSeconds = sentSamples / audioContext.sampleRate;
      heardSpeech = false;
      silenceStartedAt = 0;
      silenceReported = false;
    },
    // Drain the worker: stop feeding audio, ask for the final result, and wait
    // until the worker has processed everything sent so far. Worker messages
    // are handled in order, so a retrieveFinalResult for a bogus recognizer id
    // errors back only after all queued audio and the real final result. Any
    // late results still flow to onResult meanwhile, then listening ends.
    flush(timeoutMs = 6000) {
      if (!listening) return Promise.resolve();
      feeding = false;
      const marker = `flush-${++flushCount}`;
      return new Promise(resolve => {
        let timer;
        const finish = () => {
          clearTimeout(timer);
          model.removeEventListener('error', onError);
          listening = false;
          feeding = true;
          resolve();
        };
        const onError = event => { if (event.detail?.recognizerId === marker) finish(); };
        model.addEventListener('error', onError);
        timer = setTimeout(finish, timeoutMs);
        recognizer.retrieveFinalResult();
        model.postMessage({ action: 'retrieveFinalResult', recognizerId: marker });
      });
    },
    stop() {
      listening = false;
      feeding = true;
      heardSpeech = false;
      silenceStartedAt = 0;
      silenceReported = false;
    },
    async destroy() {
      listening = false;
      this.stopClip();
      wakeEvents.forEach(([target, name]) => target.removeEventListener(name, wake));
      processor.onaudioprocess = null;
      source.disconnect();
      processor.disconnect();
      silentOutput.disconnect();
      stream.getTracks().forEach(track => track.stop());
      recognizer.remove();
      model.terminate();
      if (audioContext.state !== 'closed') await audioContext.close();
    }
  };
}
